import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, Upload, Trash2, Play, Plus, Star, Pencil, Check, X,
  MousePointer, Link as LinkIcon, Home, Volume2, VolumeX, Info,
  Image as ImageIcon, FileText, Monitor,
} from "lucide-react";
import Header from "../components/Header";
import { api, API_BASE, extractErrorMessage } from "../lib/api";
import { useAuth, hasAccess } from "../context/AuthContext";

// ============================================================================
// Admin — Screen card
// ============================================================================
function ScreenCard({ screen, onDelete, onUpdate, onSetStart, onEditHotspots, onReplaceMedia }) {
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(screen.name);
  const [editingDesc, setEditingDesc] = useState(false);
  const [description, setDescription] = useState(screen.description || "");
  const fileInputRef = useRef(null);

  const isVideo = screen.content_type?.startsWith("video") || screen.content_type === "image/gif";
  const mediaUrl = `${API_BASE}/demo/media/${screen.file_id}`;

  return (
    <div className="bg-envicom-surface border border-envicom-border rounded-md overflow-hidden group" data-testid={`screen-card-${screen.id}`}>
      <div className="relative aspect-video bg-envicom-bg flex items-center justify-center overflow-hidden">
        {isVideo ? (
          <video src={mediaUrl} className="w-full h-full object-contain" autoPlay loop muted playsInline />
        ) : (
          <img src={mediaUrl} alt={screen.name} className="w-full h-full object-contain" />
        )}
        {screen.is_start_screen && (
          <div className="absolute top-2 left-2 bg-envicom-warning text-black text-[10px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded">
            Start
          </div>
        )}
        <span className="absolute top-2 right-2 bg-black/70 text-envicom-muted text-[10px] font-mono px-2 py-0.5 rounded">
          {screen.hotspots?.length || 0} hotspots
        </span>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept="image/*,video/mp4,video/quicktime,video/webm,.gif"
          onChange={async (e) => {
            if (e.target.files[0]) {
              await onReplaceMedia(screen.id, e.target.files[0]);
              e.target.value = "";
            }
          }}
        />
      </div>
      <div className="p-3">
        {editingName ? (
          <div className="flex gap-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="flex-1 bg-envicom-input border border-envicom-border rounded px-2 py-1 text-sm text-envicom-text font-mono"
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && (onUpdate(screen.id, { name }), setEditingName(false))}
            />
            <button type="button" onClick={() => { onUpdate(screen.id, { name }); setEditingName(false); }} className="text-envicom-green"><Check className="w-4 h-4" /></button>
            <button type="button" onClick={() => { setEditingName(false); setName(screen.name); }} className="text-envicom-muted"><X className="w-4 h-4" /></button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-envicom-text text-sm font-mono font-medium truncate">{screen.name}</span>
            <button type="button" onClick={() => setEditingName(true)} className="text-envicom-muted hover:text-envicom-text ml-2"><Pencil className="w-3 h-3" /></button>
          </div>
        )}

        <div className="mt-2">
          {editingDesc ? (
            <div className="space-y-1.5">
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full bg-envicom-input border border-envicom-border rounded px-2 py-1.5 text-xs text-envicom-text resize-none"
                rows={3}
                placeholder="Describe this screen for the demo viewer…"
                autoFocus
              />
              <div className="flex gap-1.5 justify-end">
                <button type="button" onClick={() => { onUpdate(screen.id, { description }); setEditingDesc(false); }} className="text-envicom-green text-xs flex items-center gap-1">
                  <Check className="w-3 h-3" /> Save
                </button>
                <button type="button" onClick={() => { setEditingDesc(false); setDescription(screen.description || ""); }} className="text-envicom-muted text-xs">Cancel</button>
              </div>
            </div>
          ) : (
            <button type="button" onClick={() => setEditingDesc(true)} className="w-full text-left mt-1" data-testid={`edit-desc-${screen.id}`}>
              {screen.description ? (
                <p className="text-envicom-muted text-[11px] line-clamp-2 hover:text-envicom-text">{screen.description}</p>
              ) : (
                <p className="text-envicom-muted/60 text-[11px] italic hover:text-envicom-muted flex items-center gap-1">
                  <FileText className="w-3 h-3" /> Add description…
                </p>
              )}
            </button>
          )}
        </div>

        <div className="flex gap-1.5 mt-3">
          <button
            type="button"
            onClick={() => onEditHotspots(screen)}
            className="flex-1 bg-envicom-blue/15 hover:bg-envicom-blue/30 text-envicom-blue text-xs py-1.5 rounded flex items-center justify-center gap-1 font-mono"
            data-testid={`edit-hotspots-${screen.id}`}
          >
            <MousePointer className="w-3 h-3" /> Hotspots
          </button>
          {!screen.is_start_screen && (
            <button
              type="button"
              onClick={() => onSetStart(screen.id)}
              className="bg-envicom-warning/15 hover:bg-envicom-warning/30 text-envicom-warning text-xs py-1.5 px-2 rounded"
              title="Set as start screen"
            >
              <Star className="w-3 h-3" />
            </button>
          )}
          <button type="button" onClick={() => fileInputRef.current?.click()} className="bg-envicom-surfaceHover hover:bg-envicom-input text-envicom-muted text-xs py-1.5 px-2 rounded" title="Replace media">
            <ImageIcon className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(screen.id)}
            className="bg-envicom-error/15 hover:bg-envicom-error/30 text-envicom-error text-xs py-1.5 px-2 rounded"
            data-testid={`delete-screen-${screen.id}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Hotspot editor
// ============================================================================
function HotspotEditor({ screen, screens, onSave, onClose }) {
  const [hotspots, setHotspots] = useState(screen.hotspots || []);
  const [drawing, setDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [currentRect, setCurrentRect] = useState(null);
  const [selectedHotspot, setSelectedHotspot] = useState(null);
  const [linking, setLinking] = useState(null);
  const [editingTooltip, setEditingTooltip] = useState(null);
  const containerRef = useRef(null);

  const isVideo = screen.content_type?.startsWith("video") || screen.content_type === "image/gif";
  const mediaUrl = `${API_BASE}/demo/media/${screen.file_id}`;

  const getPos = (e) => {
    const rect = containerRef.current.getBoundingClientRect();
    return { x: ((e.clientX - rect.left) / rect.width) * 100, y: ((e.clientY - rect.top) / rect.height) * 100 };
  };

  const onDown = (e) => {
    if (linking || editingTooltip) return;
    if (e.target.closest("[data-hotspot-control]")) return;
    const p = getPos(e);
    setDrawing(true);
    setDrawStart(p);
    setCurrentRect({ x: p.x, y: p.y, width: 0, height: 0 });
    setSelectedHotspot(null);
  };
  const onMove = (e) => {
    if (!drawing || !drawStart) return;
    const p = getPos(e);
    setCurrentRect({
      x: Math.min(drawStart.x, p.x),
      y: Math.min(drawStart.y, p.y),
      width: Math.abs(p.x - drawStart.x),
      height: Math.abs(p.y - drawStart.y),
    });
  };
  const onUp = () => {
    if (!drawing || !currentRect) { setDrawing(false); return; }
    setDrawing(false);
    if (currentRect.width > 1 && currentRect.height > 1) {
      const hs = { id: `hs-${Date.now()}`, ...currentRect, target_screen_id: "", label: "", tooltip: "" };
      setHotspots((prev) => [...prev, hs]);
      setSelectedHotspot(hs.id);
      setLinking(hs.id);
    }
    setCurrentRect(null);
    setDrawStart(null);
  };

  const deleteHs = (id) => {
    setHotspots((prev) => prev.filter((h) => h.id !== id));
    if (selectedHotspot === id) setSelectedHotspot(null);
    if (linking === id) setLinking(null);
    if (editingTooltip === id) setEditingTooltip(null);
  };
  const linkHs = (hsId, target) => {
    setHotspots((prev) => prev.map((h) => (h.id === hsId ? { ...h, target_screen_id: target } : h)));
    setLinking(null);
  };
  const setTooltip = (hsId, tooltip) =>
    setHotspots((prev) => prev.map((h) => (h.id === hsId ? { ...h, tooltip } : h)));

  const others = screens.filter((s) => s.id !== screen.id);

  return (
    <div className="fixed inset-0 z-50 bg-envicom-bg/95 backdrop-blur flex flex-col" data-testid="hotspot-editor">
      <div className="flex items-center justify-between px-4 py-3 bg-envicom-surface border-b border-envicom-border">
        <div className="flex items-center gap-3">
          <h3 className="text-envicom-text font-mono font-medium">Editing hotspots: {screen.name}</h3>
          <span className="text-envicom-muted text-xs">{hotspots.length} hotspot(s) — draw a rectangle, then link & add tooltips</span>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => onSave(screen.id, hotspots)} className="bg-envicom-accent hover:bg-sky-400 text-black font-semibold px-4 py-1.5 rounded text-sm font-mono" data-testid="save-hotspots-btn">
            Save
          </button>
          <button type="button" onClick={onClose} className="text-envicom-muted hover:text-envicom-text px-3 py-1.5 text-sm font-mono">Cancel</button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 flex items-center justify-center p-4 overflow-auto">
          <div
            ref={containerRef}
            className="relative select-none max-w-full max-h-full"
            style={{ cursor: linking || editingTooltip ? "default" : "crosshair" }}
            onMouseDown={onDown}
            onMouseMove={onMove}
            onMouseUp={onUp}
          >
            {isVideo ? (
              <video src={mediaUrl} className="max-w-full max-h-[calc(100vh-120px)] object-contain rounded pointer-events-none" autoPlay loop muted playsInline draggable={false} />
            ) : (
              <img src={mediaUrl} alt={screen.name} className="max-w-full max-h-[calc(100vh-120px)] object-contain rounded" draggable={false} />
            )}
            {hotspots.map((hs) => {
              const target = screens.find((s) => s.id === hs.target_screen_id);
              return (
                <div
                  key={hs.id}
                  data-hotspot-control="true"
                  className={`absolute border-2 rounded transition-colors ${
                    selectedHotspot === hs.id ? "border-envicom-accent bg-envicom-accent/25"
                    : hs.target_screen_id ? "border-envicom-green/70 bg-envicom-green/15"
                    : "border-envicom-warning/70 bg-envicom-warning/15"
                  }`}
                  style={{ left: `${hs.x}%`, top: `${hs.y}%`, width: `${hs.width}%`, height: `${hs.height}%` }}
                  onClick={(e) => { e.stopPropagation(); setSelectedHotspot(hs.id); }}
                >
                  <div className="absolute -top-5 left-0 flex gap-1 text-[10px] font-mono" data-hotspot-control="true">
                    {target ? (
                      <span className="bg-envicom-green/80 text-black px-1 rounded">{target.name}</span>
                    ) : (
                      <span className="bg-envicom-warning/80 text-black px-1 rounded">unlinked</span>
                    )}
                    <button type="button" onClick={(e) => { e.stopPropagation(); setLinking(hs.id); setEditingTooltip(null); }} className="bg-envicom-accent/80 text-black px-1 rounded hover:bg-envicom-accent" title="Link to screen">
                      <LinkIcon className="w-3 h-3 inline" />
                    </button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setEditingTooltip(hs.id); setLinking(null); }} className={`${hs.tooltip ? "bg-envicom-teal/80" : "bg-envicom-muted/60"} text-black px-1 rounded`} title="Edit tooltip">
                      <Info className="w-3 h-3 inline" />
                    </button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); deleteHs(hs.id); }} className="bg-envicom-error/80 text-white px-1 rounded">
                      <X className="w-3 h-3 inline" />
                    </button>
                  </div>
                  {hs.tooltip && (
                    <div className="absolute -bottom-4 left-0 text-[9px] text-envicom-teal/80 truncate max-w-full font-mono" data-hotspot-control="true">
                      {hs.tooltip.substring(0, 30)}{hs.tooltip.length > 30 ? "…" : ""}
                    </div>
                  )}
                </div>
              );
            })}
            {currentRect && (
              <div
                className="absolute border-2 border-envicom-accent bg-envicom-accent/20 rounded pointer-events-none"
                style={{ left: `${currentRect.x}%`, top: `${currentRect.y}%`, width: `${currentRect.width}%`, height: `${currentRect.height}%` }}
              />
            )}
          </div>
        </div>

        {(linking || editingTooltip) && (
          <div className="w-72 bg-envicom-surface border-l border-envicom-border p-4 overflow-auto" data-testid="side-panel">
            {linking && (
              <>
                <h4 className="text-envicom-text text-sm font-mono font-medium mb-1">Link to screen</h4>
                <p className="text-envicom-muted text-xs mb-3">Pick which screen this hotspot navigates to</p>
                <div className="space-y-1.5">
                  {others.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => linkHs(linking, s.id)}
                      className="w-full text-left bg-envicom-input hover:bg-envicom-surfaceHover border border-envicom-border rounded p-2 flex items-center gap-2"
                      data-testid={`link-target-${s.id}`}
                    >
                      <img src={`${API_BASE}/demo/media/${s.file_id}`} alt="" className="w-10 h-7 object-cover rounded border border-envicom-border" />
                      <span className="text-envicom-text text-xs truncate font-mono">{s.name}</span>
                    </button>
                  ))}
                  {others.length === 0 && <p className="text-envicom-muted text-xs">No other screens to link to.</p>}
                </div>
                <button type="button" onClick={() => setLinking(null)} className="mt-3 w-full text-envicom-muted hover:text-envicom-text text-xs py-1.5 border border-envicom-border rounded font-mono">Cancel</button>
              </>
            )}
            {editingTooltip && (
              <>
                <h4 className="text-envicom-text text-sm font-mono font-medium mb-1">Hotspot tooltip</h4>
                <p className="text-envicom-muted text-xs mb-3">Text shown when user hovers this hotspot in demo mode</p>
                <textarea
                  value={hotspots.find((h) => h.id === editingTooltip)?.tooltip || ""}
                  onChange={(e) => setTooltip(editingTooltip, e.target.value)}
                  className="w-full bg-envicom-input border border-envicom-border rounded px-3 py-2 text-sm text-envicom-text resize-none"
                  rows={4}
                  placeholder="e.g. Opens temperature settings…"
                  autoFocus
                  data-testid="hotspot-tooltip-input"
                />
                <button type="button" onClick={() => setEditingTooltip(null)} className="mt-2 w-full bg-envicom-teal hover:bg-teal-400 text-black text-xs py-1.5 rounded font-mono">Done</button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Demo player
// ============================================================================
function DemoPlayer({ screens, onExit }) {
  const startScreen = screens.find((s) => s.is_start_screen) || screens[0];
  const [currentScreen, setCurrentScreen] = useState(startScreen);
  const [history, setHistory] = useState([]);
  const [hoverHs, setHoverHs] = useState(null);
  const [speaking, setSpeaking] = useState(false);
  const [showDesc, setShowDesc] = useState(true);
  const synthRef = useRef(typeof window !== "undefined" ? window.speechSynthesis : null);

  const navigateTo = useCallback((id) => {
    const t = screens.find((s) => s.id === id);
    if (t) {
      setHistory((h) => [...h, currentScreen.id]);
      setCurrentScreen(t);
      setHoverHs(null);
      synthRef.current?.cancel();
      setSpeaking(false);
    }
  }, [screens, currentScreen]);

  const goBack = useCallback(() => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    const p = screens.find((s) => s.id === prev);
    if (p) { setCurrentScreen(p); setHoverHs(null); }
    synthRef.current?.cancel();
    setSpeaking(false);
  }, [history, screens]);

  const goHome = useCallback(() => {
    if (!startScreen || startScreen.id === currentScreen?.id) return;
    setHistory((h) => [...h, currentScreen.id]);
    setCurrentScreen(startScreen);
    setHoverHs(null);
    synthRef.current?.cancel();
    setSpeaking(false);
  }, [startScreen, currentScreen]);

  const handleSpeak = useCallback(() => {
    if (!currentScreen?.description || !synthRef.current) return;
    if (speaking) { synthRef.current.cancel(); setSpeaking(false); return; }
    const u = new window.SpeechSynthesisUtterance(currentScreen.description);
    u.rate = 0.95;
    const voices = synthRef.current.getVoices();
    const pref = voices.find((v) => v.lang === "en-US" && /female|samantha|zira|aria/i.test(v.name))
      || voices.find((v) => v.lang === "en-US" && !/male|david|mark|guy/i.test(v.name))
      || voices.find((v) => v.lang === "en-US");
    if (pref) u.voice = pref;
    u.lang = "en-US";
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    synthRef.current.speak(u);
    setSpeaking(true);
  }, [currentScreen, speaking]);

  useEffect(() => {
    const synth = synthRef.current;
    if (!synth) return;
    synth.getVoices();
    const handle = () => synth.getVoices();
    synth.addEventListener?.("voiceschanged", handle);
    return () => synth.removeEventListener?.("voiceschanged", handle);
  }, []);

  useEffect(() => {
    const synth = synthRef.current;
    return () => { synth?.cancel(); };
  }, []);

  useEffect(() => {
    const handle = (e) => {
      if (e.key === "Escape") { synthRef.current?.cancel(); onExit(); }
      if (e.key === "Backspace") goBack();
    };
    window.addEventListener("keydown", handle);
    return () => window.removeEventListener("keydown", handle);
  }, [onExit, goBack]);

  if (!currentScreen) return <div className="text-envicom-text p-8">No screens available.</div>;

  const isVideo = currentScreen.content_type?.startsWith("video") || currentScreen.content_type === "image/gif";
  const linkedHs = (currentScreen.hotspots || []).filter((h) => h.target_screen_id);
  const hasDesc = !!currentScreen.description;
  const mediaUrl = `${API_BASE}/demo/media/${currentScreen.file_id}`;

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="demo-player">
      <div className="flex items-center justify-between px-4 py-2 bg-envicom-bg/95 border-b border-envicom-border shrink-0">
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => { synthRef.current?.cancel(); onExit(); }} className="text-envicom-muted hover:text-envicom-text text-xs px-2.5 py-1.5 rounded bg-envicom-surface hover:bg-envicom-surfaceHover flex items-center gap-1.5 font-mono" data-testid="demo-exit-btn">
            <X className="w-3.5 h-3.5" /> Exit
          </button>
          <button type="button" onClick={goHome} disabled={!startScreen || startScreen.id === currentScreen?.id} className="text-envicom-muted hover:text-envicom-text disabled:opacity-30 text-xs px-2.5 py-1.5 rounded bg-envicom-surface flex items-center gap-1.5 font-mono" data-testid="demo-home-btn">
            <Home className="w-3.5 h-3.5" /> Home
          </button>
          <button type="button" onClick={goBack} disabled={history.length === 0} className="text-envicom-muted hover:text-envicom-text disabled:opacity-30 text-xs px-2.5 py-1.5 rounded bg-envicom-surface flex items-center gap-1.5 font-mono" data-testid="demo-back-btn">
            <ChevronLeft className="w-3.5 h-3.5" /> Back
          </button>
          {hasDesc && (
            <button type="button" onClick={() => setShowDesc((p) => !p)} className={`text-xs px-2.5 py-1.5 rounded flex items-center gap-1.5 font-mono ${showDesc ? "bg-envicom-teal/25 text-envicom-teal" : "bg-envicom-surface text-envicom-muted hover:text-envicom-text"}`} data-testid="toggle-desc-btn">
              <FileText className="w-3.5 h-3.5" /> Info
            </button>
          )}
        </div>
        <span className="text-envicom-muted text-xs font-mono">{currentScreen.name}</span>
        <span className="text-envicom-muted/70 text-[10px] font-mono">ESC to exit · Backspace = back</span>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 flex items-center justify-center overflow-hidden relative">
          <div className="relative max-w-full max-h-full">
            {isVideo ? (
              <video key={currentScreen.id} src={mediaUrl} className="max-w-full max-h-[calc(100vh-48px)] object-contain" autoPlay loop muted playsInline />
            ) : (
              <img src={mediaUrl} alt={currentScreen.name} className="max-w-full max-h-[calc(100vh-48px)] object-contain" />
            )}
            {linkedHs.map((hs) => (
              <div
                key={hs.id}
                className="absolute cursor-pointer transition-all duration-150"
                style={{
                  left: `${hs.x}%`, top: `${hs.y}%`, width: `${hs.width}%`, height: `${hs.height}%`,
                  background: hoverHs === hs.id ? "rgba(14,165,233,0.18)" : "transparent",
                  border: hoverHs === hs.id ? "1px solid rgba(14,165,233,0.5)" : "1px solid transparent",
                  borderRadius: "4px",
                }}
                onClick={() => navigateTo(hs.target_screen_id)}
                onMouseEnter={() => setHoverHs(hs.id)}
                onMouseLeave={() => setHoverHs(null)}
                data-testid={`demo-hotspot-${hs.id}`}
              >
                {hoverHs === hs.id && hs.tooltip && (
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-10 pointer-events-none" style={{ minWidth: 120, maxWidth: 280 }}>
                    <div className="bg-envicom-bg/95 border border-envicom-teal/40 text-envicom-text text-xs px-3 py-2 rounded shadow-lg backdrop-blur">
                      {hs.tooltip}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {hasDesc && showDesc && (
          <div className="w-80 bg-envicom-bg/95 border-l border-envicom-border flex flex-col shrink-0" data-testid="demo-description-panel">
            <div className="p-4 border-b border-envicom-border flex items-center justify-between">
              <h4 className="text-envicom-text text-sm font-mono font-medium flex items-center gap-2">
                <FileText className="w-4 h-4 text-envicom-teal" /> Screen Info
              </h4>
              <div className="flex items-center gap-1.5">
                <button type="button" onClick={handleSpeak} className={`p-1.5 rounded ${speaking ? "bg-envicom-teal text-black" : "bg-envicom-surface text-envicom-muted hover:text-envicom-text hover:bg-envicom-surfaceHover"}`} title={speaking ? "Stop" : "Read aloud"} data-testid="tts-btn">
                  {speaking ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
                <button type="button" onClick={() => setShowDesc(false)} className="p-1.5 bg-envicom-surface text-envicom-muted hover:text-envicom-text rounded">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <p className="text-envicom-text text-sm leading-relaxed whitespace-pre-wrap">{currentScreen.description}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Main page
// ============================================================================
export default function EnvicomDemoApp() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = hasAccess(user, "envicom_demo", true);

  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("admin");
  const [editingScreen, setEditingScreen] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const importInputRef = useRef(null);

  const fetchScreens = useCallback(async () => {
    try {
      const { data } = await api.get("/demo/screens");
      setScreens(data);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchScreens();
    if (!isAdmin) setMode("demo");
  }, [fetchScreens, isAdmin]);

  const handleUpload = async (files) => {
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("name", file.name.replace(/\.[^.]+$/, ""));
        await api.post("/demo/screens", fd);
      }
      toast.success(`${files.length} screen(s) uploaded`);
      fetchScreens();
    } catch (err) {
      toast.error("Upload failed: " + extractErrorMessage(err));
    } finally {
      setUploading(false);
    }
  };

  const handleImportPptx = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/demo/import-pptx", fd);
      toast.success(`Imported ${data.imported_count} screens from PPTX`);
      fetchScreens();
    } catch (err) {
      toast.error("Import failed: " + extractErrorMessage(err));
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this screen and its hotspots?")) return;
    try {
      await api.delete(`/demo/screens/${id}`);
      toast.success("Screen deleted");
      fetchScreens();
    } catch { toast.error("Delete failed"); }
  };

  const handleUpdate = async (id, data) => {
    try { await api.put(`/demo/screens/${id}`, data); fetchScreens(); }
    catch { toast.error("Update failed"); }
  };

  const handleSetStart = async (id) => {
    try { await api.put(`/demo/screens/${id}`, { is_start_screen: true }); toast.success("Start screen updated"); fetchScreens(); }
    catch { toast.error("Failed to set start screen"); }
  };

  const handleReplaceMedia = async (id, file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.put(`/demo/screens/${id}/media`, fd);
      toast.success("Media replaced");
      fetchScreens();
    } catch (err) {
      toast.error("Replace failed: " + extractErrorMessage(err));
    }
  };

  const handleSaveHotspots = async (screenId, hotspots) => {
    try {
      await api.put(`/demo/screens/${screenId}`, { hotspots });
      toast.success("Hotspots saved");
      setEditingScreen(null);
      fetchScreens();
    } catch { toast.error("Failed to save hotspots"); }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm("Delete ALL screens? This cannot be undone.")) return;
    try {
      for (const s of screens) await api.delete(`/demo/screens/${s.id}`);
      toast.success("All screens deleted");
      fetchScreens();
    } catch { toast.error("Failed to delete screens"); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-envicom-muted font-mono text-sm">Loading…</div>
    );
  }

  if (!isAdmin && screens.length === 0) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Monitor className="w-12 h-12 text-envicom-muted mx-auto mb-4" />
            <h3 className="text-envicom-text text-lg mb-2 font-mono">Demo not available yet</h3>
            <p className="text-envicom-muted text-sm mb-6">The interactive demo is being set up. Check back soon.</p>
            <button type="button" onClick={() => navigate("/")} className="text-envicom-accent hover:underline text-sm flex items-center gap-1 mx-auto font-mono">
              <ChevronLeft className="w-4 h-4" /> Back to hub
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (mode === "demo" && screens.length > 0) {
    return <DemoPlayer screens={screens} onExit={() => (isAdmin ? setMode("admin") : navigate("/"))} />;
  }

  if (editingScreen) {
    return <HotspotEditor screen={editingScreen} screens={screens} onSave={handleSaveHotspots} onClose={() => setEditingScreen(null)} />;
  }

  // Admin grid
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10 relative z-10">
        <Link to="/" data-testid="demo-back-link" className="inline-flex items-center gap-1 text-envicom-muted hover:text-envicom-text font-mono text-sm">
          <ChevronLeft className="w-4 h-4" /> Back to tools
        </Link>

        <div className="mt-4 flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded flex items-center justify-center bg-envicom-orange/15 text-envicom-orange border border-envicom-orange/30">
              <Play className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-mono text-2xl md:text-3xl font-bold tracking-tight">enviCoM Demo</h1>
              <p className="text-envicom-muted text-sm">Interactive controller walkthrough — manage screens and clickable hotspots.</p>
            </div>
          </div>
          {screens.length > 0 && (
            <button type="button" onClick={() => setMode("demo")} className="bg-envicom-green hover:bg-emerald-400 text-black font-mono font-semibold px-4 py-2 rounded-md text-sm flex items-center gap-2" data-testid="start-demo-btn">
              <Play className="w-4 h-4" /> Launch demo
            </button>
          )}
        </div>

        <div className="mt-8 flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-envicom-text font-mono font-medium">Screens ({screens.length})</h2>
          <div className="flex items-center gap-2">
            <input ref={fileInputRef} type="file" multiple className="hidden" accept="image/*,video/mp4,video/quicktime,video/webm,.gif"
              onChange={(e) => { handleUpload(Array.from(e.target.files)); e.target.value = ""; }} />
            <input ref={importInputRef} type="file" className="hidden" accept=".pptx"
              onChange={(e) => { handleImportPptx(e.target.files[0]); e.target.value = ""; }} />
            <button type="button" onClick={() => importInputRef.current?.click()} disabled={importing} className="bg-envicom-purple/20 hover:bg-envicom-purple/30 text-envicom-purple border border-envicom-purple/30 px-3 py-2 rounded-md text-sm flex items-center gap-2 font-mono" data-testid="import-pptx-btn">
              <Upload className="w-4 h-4" /> {importing ? "Importing…" : "Import PPTX"}
            </button>
            <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="bg-envicom-accent hover:bg-sky-400 text-black font-semibold px-3 py-2 rounded-md text-sm flex items-center gap-2 font-mono" data-testid="upload-screen-btn">
              <Plus className="w-4 h-4" /> {uploading ? "Uploading…" : "Add screen"}
            </button>
            {screens.length > 0 && (
              <button type="button" onClick={handleDeleteAll} className="bg-envicom-error/15 hover:bg-envicom-error/30 text-envicom-error px-3 py-2 rounded-md text-sm" data-testid="delete-all-btn">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {screens.length === 0 ? (
          <div className="mt-8 text-center py-20 border border-dashed border-envicom-border rounded-md">
            <Monitor className="w-12 h-12 text-envicom-muted mx-auto mb-4" />
            <h3 className="text-envicom-text text-lg mb-2 font-mono">No screens yet</h3>
            <p className="text-envicom-muted text-sm mb-6 max-w-md mx-auto">
              Upload controller screenshots, GIFs, or videos to build your interactive demo.
              You can also import all images from a PPTX in one shot.
            </p>
            <div className="flex gap-3 justify-center">
              <button type="button" onClick={() => importInputRef.current?.click()} className="bg-envicom-purple hover:bg-purple-400 text-black font-semibold px-4 py-2 rounded-md text-sm flex items-center gap-2 font-mono">
                <Upload className="w-4 h-4" /> Import from PPTX
              </button>
              <button type="button" onClick={() => fileInputRef.current?.click()} className="bg-envicom-accent hover:bg-sky-400 text-black font-semibold px-4 py-2 rounded-md text-sm flex items-center gap-2 font-mono">
                <Plus className="w-4 h-4" /> Upload files
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {screens.map((s) => (
              <ScreenCard
                key={s.id}
                screen={s}
                onDelete={handleDelete}
                onUpdate={handleUpdate}
                onSetStart={handleSetStart}
                onEditHotspots={(sc) => setEditingScreen(sc)}
                onReplaceMedia={handleReplaceMedia}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
